import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { useIntl } from 'react-intl';
import { Transition } from 'react-transition-group';
import PropTypes from 'prop-types';
import classNames from 'classnames/bind';

// State
import { fetchOrderListIsNeeded } from '../../state/modules/order/actions';
import { isOrderDoneSelector } from '../../state/modules/order/selectors';
import orderApi from '../../state/modules/order/api';

// Utils
import isomorphicCookies from '../../utils/isomorphicCookies';
import { getUnauthorizedToken } from '../../utils/auth';

// Router
import { getRoute } from '../../entry/routes';

// Icons
import TimerGreen from '../../icons/timer-green.svg';
import Close from '../../icons/close.svg';

// Styles
import styles from './ThanksForOrderingLink.styl';

const cx = classNames.bind(styles);
const TERMINAL_STATUSES = [400, 401, 403, 404];
const getOrderStatusMessage = status => {
  switch (status) {
    case 0:
    case 1:
      return 'orderStatus.status.taken';
    case 2:
      return 'orderStatus.status.prepared';
    case 3:
      return 'orderStatus.status.ready';
    case 4:
      return 'orderStatus.status.inWay';
    default:
      return 'orderStatus.status.taken';
  }
};

function dropLastOrderCookie() {
  const cookies = isomorphicCookies();

  // 1) если есть remove — пробуем с path
  if (typeof cookies.remove === 'function') {
    try {
      cookies.remove('last_order_id', { path: '/' });
    } catch (e) {}
    try {
      cookies.remove('last_order_id');
    } catch (e) {}
  }

  // 2) универсальный способ: перезаписать с истёкшим expires
  try {
    cookies.set('last_order_id', '', { expires: -1, path: '/' });
  } catch (e) {}

  // 3) иногда помогает ещё и без path (зависит от реализации)
  try {
    cookies.set('last_order_id', '', { expires: -1 });
  } catch (e) {}
}

export default function ThanksForOrderingLink(props) {
  const { className } = props;

  const intl = useIntl();
  const dispatch = useDispatch();
  const history = useHistory();
  const timeLeftIntervalRef = useRef(null);
  const intervalRef = useRef(null);

  const orderNotFoundRef = useRef(false);

  const lastOrderAuthId = useSelector(state => state.order.list[0]?.id);
  const isAuthenticated = useSelector(state => state.user.isAuthenticated);
  const isHomeCategoriesStick = useSelector(state => state.ui.isHomeCategoriesStick);
  const { isPhone, isTablet } = useSelector(state => state.responsive);

  const [orderData, setOrderData] = useState(null);
  const [timeLeft, setTimeLeft] = useState(null);
  const [isOpen, setIsOpen] = useState(() => {
    if (!isAuthenticated) {
      return true;
    }

    const thx_link_closed = isomorphicCookies().get('thx_link_closed');
    return thx_link_closed !== '1';
  });

  const isOrderDone = isOrderDoneSelector(orderData);

  const lastOrderId = useMemo(() => {
    if (isAuthenticated) {
      return lastOrderAuthId || null;
    }

    return getUnauthorizedToken() ? isomorphicCookies().get('last_order_id') : null;
  }, [lastOrderAuthId, isAuthenticated]);

  const isMobile = isPhone || isTablet;

  const currentDeliveryTime = useMemo(() => {
    if (!timeLeft) {
      return null;
    }

    const minutes = Math.floor(timeLeft / 60);
    const seconds = timeLeft % 60;
    const secondsWithZero = seconds > 9 ? seconds : `0${seconds}`;

    return `${minutes}:${secondsWithZero}`;
  }, [timeLeft]);

  const handleClick = () => {
    history.push(getRoute('orderTrackingAlias', lastOrderId));
  };

  const handleClose = () => {
    isomorphicCookies().set('thx_link_closed', '1', { expires: 1 });
    setIsOpen(false);
  };

  useEffect(() => {
    setOrderData(null);
    setTimeLeft(null);
    orderNotFoundRef.current = false; // при смене auth состояния сбрасываем
  }, [isAuthenticated]);

  // подтягиваем историю заказов, если авторизовались и ее еще нет
  useEffect(() => {
    if (isAuthenticated) {
      dispatch(fetchOrderListIsNeeded());
    }
  }, [isAuthenticated]);

  useEffect(() => {
    // если нет id / заказ завершён / уже знаем что 404 — ничего не делаем
    if (!lastOrderId || isOrderDone || orderNotFoundRef.current) {
      return () => {};
    }

    const stopPollingBecauseNotFound = () => {
      orderNotFoundRef.current = true;

      // стопаем поллинг
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }

      // стопаем таймер
      if (timeLeftIntervalRef.current) {
        clearInterval(timeLeftIntervalRef.current);
        timeLeftIntervalRef.current = null;
      }

      // чистим состояние + удаляем cookie
      setOrderData(null);
      setTimeLeft(null);
      dropLastOrderCookie();
    };

    const getOrderStatus = () => {
      if (orderNotFoundRef.current) return;

      orderApi
        .getOrderStatus({ action: 'status', order_id: lastOrderId })
        .then(data => {
          if (
            data?.success === false &&
            (Number(data?.status) === 404 || Number(data?.status) === 403)
          ) {
            stopPollingBecauseNotFound();
            return;
          }

          setOrderData({ ...data });
        })
        .catch(err => {
          const httpStatus = Number(err?.status);
          if (TERMINAL_STATUSES.includes(httpStatus)) {
            stopPollingBecauseNotFound();
          }
        });
    };

    intervalRef.current = setInterval(getOrderStatus, 60000);

    getOrderStatus();

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [lastOrderId, isOrderDone]);

  // если к нам пришло новое оставшее время, отличное от того, что у нас уже записано
  useEffect(() => {
    setTimeLeft(orderData?.time_left);

    if (orderData?.time_left) {
      if (timeLeftIntervalRef.current) {
        clearInterval(timeLeftIntervalRef.current);
      }

      timeLeftIntervalRef.current = setInterval(() => {
        setTimeLeft(prev => (typeof prev === 'number' ? Math.max(prev - 1, 0) : prev));
      }, 1000);
    }

    return () => {
      if (timeLeftIntervalRef.current) {
        clearInterval(timeLeftIntervalRef.current);
        timeLeftIntervalRef.current = null;
      }
    };
  }, [orderData?.time_left]);

  // если время закончилось, то очищаем память
  useEffect(() => {
    if (timeLeft === 0 && timeLeftIntervalRef.current) {
      clearInterval(timeLeftIntervalRef.current);
      timeLeftIntervalRef.current = null;
    }
  }, [timeLeft]);

  // если мы не нажали cancel, заказ есть и он находится в работе
  // в мобильной версии ориентируемся на положение скролла, а в десктопе показываем всегда
  const isVisible =
    isOpen &&
    orderData &&
    !isOrderDone &&
    !orderNotFoundRef.current &&
    (isMobile ? !isHomeCategoriesStick : true);

  return (
    <Transition in={isVisible} timeout={{ enter: 0, exit: 175 }} unmountOnExit mountOnEnter>
      {state => (
        <div className={cx('ThanksForOrderingLink', className, `ThanksForOrderingLink_${state}`)}>
          <button
            className={cx('ThanksForOrderingLink__link', className)}
            onClick={handleClick}
            onKeyDown={e => e.key === 'Enter' && handleClick()}
            type="button"
          >
            <TimerGreen className={cx('ThanksForOrderingLink__link-icon')} />
            <span className={cx('ThanksForOrderingLink__link-description')}>
              {orderData?.order_status !== undefined
                ? intl.formatMessage({ id: getOrderStatusMessage(Number(orderData.order_status)) })
                : ''}
              {currentDeliveryTime ? ` | ${currentDeliveryTime}` : ''}
            </span>
          </button>
          <button
            className={cx('ThanksForOrderingLink__cancel')}
            onClick={handleClose}
            onKeyDown={e => e.key === 'Enter' && handleClose()}
            type="button"
          >
            <Close className={cx('ThanksForOrderingLink__cancel-icon')} />
          </button>
        </div>
      )}
    </Transition>
  );
}

ThanksForOrderingLink.defaultProps = {
  className: '',
};

ThanksForOrderingLink.propTypes = {
  className: PropTypes.string,
};
